import type { Home, Quiz, Section } from '@efdigital/ll-addon-data';
import { selectValue } from '@efdigital/ll-addon-ui/modifiers/select-value';
import { setAsyncHasMany } from '@efdigital/ll-admin-v2/data/async-setter';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import DictionaryEditor from '../dictionary-editor.gts';

interface Signature {
  Args: {
    home: Home;
    sections: Section[];
    quizzes: Quiz[];
    uniqueId: string;
    isReadOnly: boolean;
  };
}

const DEV_MAPPER_KEYS = [
  {
    key: 'devMapperLink',
    label: 'Development mapper link',
    placeholder: '(link to mapper)',
  },
  {
    key: 'devMapperTitle',
    label: 'Development mapper heading',
    placeholder: 'Development mapper',
  },
  {
    key: 'devMapperIntro',
    label: 'Not completed message',
    placeholder:
      'The objective of the Development Mapper is to help you identify your key development needs in relation to critical behaviours you wish to develop.',
  },
  {
    key: 'devMapperExplanation',
    label: 'Completed message',
    placeholder:
      'The visual below provides an overview of your key development areas. It compares what you have identified as the most important development themes with your ratings of capability aligned to our core topic areas.',
  },
  {
    key: 'devMapperLaunch',
    label: 'Launch quiz button text',
    placeholder: 'Identify development needs',
  },
  {
    key: 'devMapperResults',
    label: 'Results button text',
    placeholder: 'View full results',
  },
];

function asArray<T>(value: unknown): T[] {
  if (Array.isArray(value)) {
    return value as T[];
  }

  if (value && typeof value === 'object' && 'toArray' in value) {
    try {
      return (value as { toArray: () => T[] }).toArray?.() ?? [];
    } catch {
      return [];
    }
  }

  return [];
}

class HomeSection extends Component<Signature> {
  @tracked selectedQuizId = '';
  @tracked selectedSectionId = '';

  get isReadOnly() {
    return this.args.isReadOnly;
  }

  get mappers(): Quiz[] {
    return asArray<Quiz>(this.args.home.mappers);
  }

  get competencies(): Section[] {
    return asArray<Section>(this.args.home.comps);
  }

  get availableQuizzes(): Quiz[] {
    const selected = new Set(this.mappers.map((quiz) => quiz.id));
    return this.args.quizzes.filter((quiz) => !selected.has(quiz.id));
  }

  get availableSections(): Section[] {
    const selected = new Set(this.competencies.map((section) => section.id));
    return this.args.sections.filter((section) => !selected.has(section.id));
  }

  get disableAddQuiz(): boolean {
    return this.selectedQuizId.length === 0;
  }

  get disableAddSection(): boolean {
    return this.selectedSectionId.length === 0;
  }

  updateSelectedQuiz = (event: Event) => {
    this.selectedQuizId = (event.target as HTMLSelectElement).value;
  };

  updateSelectedSection = (event: Event) => {
    this.selectedSectionId = (event.target as HTMLSelectElement).value;
  };

  addQuiz = () => {
    if (this.isReadOnly || this.disableAddQuiz) {
      return;
    }

    const quiz = this.args.quizzes.find(
      (item) => item.id === this.selectedQuizId
    );
    if (!quiz) {
      return;
    }

    const next = [...this.mappers, quiz];
    setAsyncHasMany(this.args.home, 'mappers', next);
    this.selectedQuizId = '';
  };

  removeQuiz = (quiz: Quiz) => {
    if (this.isReadOnly) {
      return;
    }

    const next = this.mappers.filter((item) => item.id !== quiz.id);
    setAsyncHasMany(this.args.home, 'mappers', next);
  };

  addSection = () => {
    if (this.isReadOnly || this.disableAddSection) {
      return;
    }

    const section = this.args.sections.find(
      (item) => item.id === this.selectedSectionId
    );
    if (!section) {
      return;
    }

    const next = [...this.competencies, section];
    setAsyncHasMany(this.args.home, 'comps', next);
    this.selectedSectionId = '';
  };

  removeSection = (section: Section) => {
    if (this.isReadOnly) {
      return;
    }

    const next = this.competencies.filter((item) => item.id !== section.id);
    setAsyncHasMany(this.args.home, 'comps', next);
  };

  <template>
    <h3 class="mb-4">Client Home</h3>

    <fieldset class="mb-5" disabled={{this.isReadOnly}}>
      <legend class="gud-eyebrow">Development Mapper</legend>
      <p class="text-muted">
        Curate the copy and linked quizzes shown in the client home "Development
        mapper" cards.
      </p>

      <DictionaryEditor
        @item={{@home}}
        @keys={{DEV_MAPPER_KEYS}}
        @isReadOnly={{this.isReadOnly}}
      />

      <div class="mt-4">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">Linked quizzes</h5>
          {{#unless this.isReadOnly}}
            <div class="d-flex gap-2">
              <select
                class="form-select"
                aria-label="Select quiz to add"
                {{selectValue this.selectedQuizId}}
                {{on "change" this.updateSelectedQuiz}}
              >
                <option value="">Select a quiz…</option>
                {{#each this.availableQuizzes as |quiz|}}
                  <option value={{quiz.id}}>{{quiz.title}}</option>
                {{/each}}
              </select>
              <button
                type="button"
                class="btn btn-outline-primary"
                {{on "click" this.addQuiz}}
                disabled={{this.disableAddQuiz}}
              >Add</button>
            </div>
          {{/unless}}
        </div>

        {{#if this.mappers.length}}
          <ul class="list-group">
            {{#each this.mappers as |quiz|}}
              <li
                class="list-group-item d-flex justify-content-between align-items-center"
              >
                <span>{{quiz.title}}</span>
                {{#unless this.isReadOnly}}
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    {{on "click" (fn this.removeQuiz quiz)}}
                  >
                    <iconify-icon inline icon="ph:trash" />
                    Remove
                  </button>
                {{/unless}}
              </li>
            {{/each}}
          </ul>
        {{else}}
          <p class="text-muted mb-0">No quizzes linked yet.</p>
        {{/if}}
      </div>
    </fieldset>

    <fieldset class="mb-4" disabled={{this.isReadOnly}}>
      <legend class="gud-eyebrow">Competencies</legend>
      <p class="text-muted">
        Choose the competency sections that appear on the client home page.
      </p>

      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0">Assigned sections</h5>
        {{#unless this.isReadOnly}}
          <div class="d-flex gap-2">
            <select
              class="form-select"
              aria-label="Select competency section to add"
              {{selectValue this.selectedSectionId}}
              {{on "change" this.updateSelectedSection}}
            >
              <option value="">Select a section…</option>
              {{#each this.availableSections as |section|}}
                <option value={{section.id}}>{{section.title}}</option>
              {{/each}}
            </select>
            <button
              type="button"
              class="btn btn-outline-primary"
              {{on "click" this.addSection}}
              disabled={{this.disableAddSection}}
            >Add</button>
          </div>
        {{/unless}}
      </div>

      {{#if this.competencies.length}}
        <ul class="list-group">
          {{#each this.competencies as |section|}}
            <li
              class="list-group-item d-flex justify-content-between align-items-center"
            >
              <span>{{section.title}}</span>
              {{#unless this.isReadOnly}}
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  {{on "click" (fn this.removeSection section)}}
                >
                  <iconify-icon inline icon="ph:trash" />
                  Remove
                </button>
              {{/unless}}
            </li>
          {{/each}}
        </ul>
      {{else}}
        <p class="text-muted mb-0">No competency sections selected.</p>
      {{/if}}
    </fieldset>
  </template>
}

export default HomeSection;
